import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Row,
  Col,
  Tag,
  Input,
  Select,
  message,
  Modal,
  Spin,
  Divider,
  Tooltip
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  EditOutlined,
  ReloadOutlined,
  SaveOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  MinusCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { UserService } from '../../../services/userService';
import { useAuth } from '../../../contexts/AuthContext';
import type { User, Program, UserPermission } from '../../../types/user';
import { PERMISSION_ACTIONS } from '../../../types/user';

const { Title, Text } = Typography;
const { Search } = Input;

interface PersonalPermissionsState {
  users: User[];
  loading: boolean;
  searchText: string;
  filterDepartment: string | null;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  // Modal相關狀態
  modalVisible: boolean;
  selectedUser: User | null;
  programs: Program[];
  permissions: UserPermission[];
  modalLoading: boolean;
  saving: boolean;
}

const PersonalPermissionsManagement: React.FC = () => {
  const navigate = useNavigate();
  const { refreshPermissions } = useAuth();
  const [permSearchText, setPermSearchText] = useState('');

  const [state, setState] = useState<PersonalPermissionsState>({
    users: [],
    loading: false,
    searchText: '',
    filterDepartment: null,
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
    // Modal相關狀態初始化
    modalVisible: false,
    selectedUser: null,
    programs: [],
    permissions: [],
    modalLoading: false,
    saving: false,
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));
      const response = await UserService.listUsers({ 
        limit: state.pagination.pageSize,
        page: state.pagination.current,
        search: state.searchText,
        dptNo: state.filterDepartment || undefined
      });
      setState(prev => ({
        ...prev,
        users: response.items,
        pagination: {
          ...prev.pagination,
          total: response.pagination.total
        },
        loading: false,
      }));
    } catch (error) {
      message.error('載入使用者清單失敗');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const handleSearch = (value: string) => {
    setState(prev => ({ 
      ...prev, 
      searchText: value,
      pagination: { ...prev.pagination, current: 1 }
    }));
    setTimeout(loadUsers, 0);
  };

  const handleDepartmentChange = (value: string | null) => {
    setState(prev => ({ 
      ...prev, 
      filterDepartment: value,
      pagination: { ...prev.pagination, current: 1 }
    }));
    setTimeout(loadUsers, 0);
  };

  const handleTableChange = (pagination: any) => {
    setState(prev => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      }
    }));
    setTimeout(loadUsers, 0);
  };

  const handleEditPermissions = async (userId: string) => {
    const user = state.users.find(u => u.userId === userId);
    if (!user) {
      message.error('找不到使用者資料');
      return;
    }

    setState(prev => ({ 
      ...prev, 
      selectedUser: user,
      modalVisible: true,
      modalLoading: true 
    }));

    try {
      // 同時載入程式清單和使用者權限
      const [programs, permissions] = await Promise.all([
        UserService.listPrograms(),
        UserService.getUserPermissions(userId)
      ]);

      console.log('Programs loaded:', programs);
      console.log('Permissions loaded:', permissions);
      console.log('Permissions structure:', permissions);
      console.log('Permissions.permissions:', permissions?.permissions);

      // 建立完整的權限列表
      const existingPermissions = permissions?.permissions || [];
      console.log('Existing permissions:', existingPermissions);
      
      const fullPermissions = programs.map(program => {
        const existing = existingPermissions.find((p: any) => p.programId === program.pgId);
        if (existing) {
          return existing;
        }
        // 如果沒有現有權限，建立預設權限（全部為 null = 未設定/繼承群組）
        return {
          programId: program.pgId,
          programName: program.pgName,
          query: null,
          addNew: null,
          edit: null,
          delete: null,
          export: null,
          print: null,
          approve: null
        };
      });

      console.log('Full permissions after processing:', fullPermissions);

      setState(prev => ({
        ...prev,
        programs,
        permissions: fullPermissions,
        modalLoading: false,
      }));
    } catch (error) {
      console.error('載入權限資料時發生錯誤:', error);
      message.error(`載入權限資料失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ 
        ...prev, 
        modalLoading: false,
        modalVisible: false 
      }));
    }
  };

  // 三態循環：null(未設定) → true(開) → false(關) → null(未設定)
  const cyclePermission = (current: boolean | null): boolean | null => {
    if (current === null) return true;
    if (current === true) return false;
    return null;
  };

  // 處理權限變更
  const handlePermissionChange = (programId: number, permissionType: keyof UserPermission) => {
    setState(prev => {
      const newPermissions = [...prev.permissions];
      const existingIndex = newPermissions.findIndex(p => p.programId === programId);

      if (existingIndex !== -1) {
        const currentValue = newPermissions[existingIndex][permissionType] as boolean | null;
        newPermissions[existingIndex] = {
          ...newPermissions[existingIndex],
          [permissionType]: cyclePermission(currentValue)
        };
      } else {
        const program = state.programs.find(p => p.pgId === programId);
        if (program) {
          const newPermission: UserPermission = {
            programId,
            programName: program.pgName,
            query: null,
            addNew: null,
            edit: null,
            delete: null,
            export: null,
            print: null,
            approve: null,
            [permissionType]: true
          };
          newPermissions.push(newPermission);
        }
      }

      return { ...prev, permissions: newPermissions };
    });
  };

  // 儲存權限變更
  const handleSavePermissions = async () => {
    if (!state.selectedUser) {
      message.error('沒有選擇使用者');
      return;
    }
    
    try {
      setState(prev => ({ ...prev, saving: true }));
      
      // 只儲存有任何明確設定（非全 null）的項目
      const permissionsToSave = state.permissions.filter(p =>
        p.query !== null || p.addNew !== null || p.edit !== null ||
        p.delete !== null || p.export !== null || p.print !== null || p.approve !== null
      );
      
      await UserService.setUserPermissions(state.selectedUser.userId, { 
        permissions: permissionsToSave 
      });
      
      message.success('個人權限更新成功');
      await refreshPermissions();
      setState(prev => ({
        ...prev,
        modalVisible: false,
        saving: false,
        selectedUser: null,
        programs: [],
        permissions: []
      }));
    } catch (error) {
      message.error(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  // 關閉Modal
  const handleCloseModal = () => {
    setState(prev => ({ 
      ...prev, 
      modalVisible: false,
      selectedUser: null,
      programs: [],
      permissions: [],
      modalLoading: false,
      saving: false
    }));
  };

  // 渲染程式權限設定表格
  const renderProgramPermissions = (program: Program, level: number = 0) => {
    const permission = state.permissions.find(p => p.programId === program.pgId);
    
    return (
      <React.Fragment key={program.pgId}>
        <Row 
          align="middle" 
          style={{ 
            padding: '8px 16px',
            borderBottom: '1px solid #f0f0f0',
            backgroundColor: level % 2 === 0 ? '#fafafa' : '#ffffff'
          }}
        >
          <Col span={6}>
            <Text style={{ paddingLeft: `${level * 16}px`, fontWeight: level === 0 ? 'bold' : 'normal' }}>
              {level > 0 && '└─ '}{program.pgName}
            </Text>
          </Col>
          {Object.entries(PERMISSION_ACTIONS).map(([key, action]) => {
            const value = (permission?.[action as keyof UserPermission] as boolean | null) ?? null;

            let icon: React.ReactNode;
            let color: string;
            let tooltip: string;
            if (value === null) {
              icon = <MinusCircleOutlined />;
              color = '#d9d9d9';
              tooltip = '未設定（繼承群組）';
            } else if (value === true) {
              icon = <CheckCircleFilled />;
              color = '#52c41a';
              tooltip = '明確允許';
            } else {
              icon = <CloseCircleFilled />;
              color = '#ff4d4f';
              tooltip = '明確禁止（覆蓋群組）';
            }

            return (
              <Col span={3} key={action} style={{ textAlign: 'center' }}>
                <Tooltip title={tooltip}>
                  <Button
                    type="text"
                    size="small"
                    icon={icon}
                    style={{ color }}
                    onClick={() => handlePermissionChange(program.pgId, action as keyof UserPermission)}
                  />
                </Tooltip>
              </Col>
            );
          })}
        </Row>
        {program.children && program.children.map(child => renderProgramPermissions(child, level + 1))}
      </React.Fragment>
    );
  };

  // 獲取部門列表
  const departments = Array.from(new Set(state.users
    .filter(u => u.department)
    .map(u => u.department!.dptNo)))
    .map(dptNo => {
      const dept = state.users.find(u => u.department?.dptNo === dptNo)?.department;
      return { dptNo, dptName: dept?.dptName || dptNo };
    });

  // User table columns
  const columns: ColumnsType<User> = [
    {
      title: '使用者ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 120,
    },
    {
      title: '使用者名稱',
      dataIndex: 'userName',
      key: 'userName',
      width: 150,
    },
    {
      title: '部門',
      key: 'department',
      width: 120,
      render: (_, record) => record.department?.dptName || 'N/A',
    },
    {
      title: '電子郵件',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (email: string) => email || 'N/A',
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: string) => (
        <Tag color={status === 'A' ? 'green' : 'red'}>
          {status === 'A' ? '啟用' : '停用'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditPermissions(record.userId)}
        >
          編輯權限
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>個人權限管理</Title>
          <Text type="secondary">管理使用者的個人權限設定</Text>
        </Col>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadUsers}
            size="large"
          >
            重新整理
          </Button>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
          <Col xs={24} md={8}>
            <Search
              placeholder="搜尋使用者ID或姓名"
              allowClear
              size="large"
              onSearch={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="選擇部門"
              allowClear
              size="large"
              style={{ width: '100%' }}
              value={state.filterDepartment}
              onChange={handleDepartmentChange}
            >
              {departments.map(dept => (
                <Select.Option key={dept.dptNo} value={dept.dptNo}>
                  {dept.dptName}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={state.users}
          rowKey="userId"
          loading={state.loading}
          pagination={{
            ...state.pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 筆`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* 個人權限編輯Modal */}
      <Modal
        title={`設定個人權限 - ${state.selectedUser?.userName || ''}`}
        open={state.modalVisible}
        onCancel={handleCloseModal}
        footer={[
          <Button key="cancel" onClick={handleCloseModal}>
            取消
          </Button>,
          <Button 
            key="ok" 
            type="primary" 
            onClick={handleSavePermissions} 
            loading={state.saving}
            icon={<SaveOutlined />}
          >
            儲存
          </Button>,
        ]}
        width={1000}
        destroyOnClose
      >
        {state.modalLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}>載入權限資料中...</div>
          </div>
        ) : (
          <>
            {state.selectedUser && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <Text strong>使用者資訊：</Text>
                <Space split={<Divider type="vertical" />}>
                  <Text>ID: {state.selectedUser.userId}</Text>
                  <Text>姓名: {state.selectedUser.userName}</Text>
                  <Text>部門: {state.selectedUser.department?.dptName || 'N/A'}</Text>
                </Space>
              </div>
            )}
            
            <Input.Search
              placeholder="搜尋程式名稱..."
              allowClear
              style={{ marginBottom: 12 }}
              onChange={e => setPermSearchText(e.target.value)}
              prefix={<SearchOutlined />}
            />

            <div style={{ maxHeight: '500px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
              {/* 表頭 */}
              <Row
                align="middle"
                style={{
                  padding: '12px 16px',
                  borderBottom: '2px solid #e6f7ff',
                  backgroundColor: '#e6f7ff',
                  fontWeight: 'bold'
                }}
              >
                <Col span={6}>
                  <Text strong>程式名稱</Text>
                </Col>
                {Object.keys(PERMISSION_ACTIONS).map((key) => (
                  <Col span={3} key={key} style={{ textAlign: 'center' }}>
                    <Text strong>{key.replace('_', '')}</Text>
                  </Col>
                ))}
              </Row>

              {/* 程式權限列表 */}
              {(() => {
                const filterProgs = (progs: Program[], search: string): Program[] => {
                  if (!search) return progs;
                  const lower = search.toLowerCase();
                  return progs.reduce<Program[]>((acc, p) => {
                    const match = p.pgName.toLowerCase().includes(lower) || (p.pgCode || '').toLowerCase().includes(lower);
                    const filteredChildren = p.children ? filterProgs(p.children, search) : [];
                    if (match || filteredChildren.length > 0) {
                      acc.push({ ...p, children: match ? p.children : filteredChildren });
                    }
                    return acc;
                  }, []);
                };
                const filtered = filterProgs(state.programs, permSearchText);
                return filtered.length > 0 ? (
                  filtered.map(program => renderProgramPermissions(program))
              ) : (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Text type="secondary">沒有符合的程式</Text>
                </div>
              );
              })()}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default PersonalPermissionsManagement;